import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { getDeviceId } from '@utils/axiosInstance';
import LoadingComponent from '@components/_commons/LoadingComponent';
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import Computer from '@mui/icons-material/Computer';
import Phone from '@mui/icons-material/Phone';
import Tablet from '@mui/icons-material/Tablet';
import Laptop from '@mui/icons-material/Laptop';
import Delete from '@mui/icons-material/Delete';
import Refresh from '@mui/icons-material/Refresh';
import Close from '@mui/icons-material/Close';

function Security() {
    const { t } = useTranslation();
    const [devices, setDevices] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [removingDevices, setRemovingDevices] = useState<Set<string>>(new Set());
    const [deviceErrors, setDeviceErrors] = useState<Record<string, string>>({});

    // User Agent에서 OS 정보 추출하는 함수
    const getOSFromUserAgent = (userAgent: string): string => {
        console.log('userAgent', userAgent);
        if (userAgent.includes('Windows')) return 'Windows';
        if (userAgent.includes('Mac OS')) return 'MacOS';
        if (userAgent.includes('Linux')) return 'Linux';
        if (userAgent.includes('Android')) return 'Android';
        if (userAgent.includes('iOS')) return 'iOS';
        return 'Unknown';
    };

    // OS별 아이콘 컴포넌트
    const getOSIcon = (os: string) => {
        switch (os) {
            case 'Windows':
            case 'MacOS':
            case 'Linux':
                return <Computer />;
            case 'Android':
                return <Phone />;
            case 'iOS':
                return <Tablet />;
            default:
                return <Laptop />;
        }
    };

    // 디바이스 타입 추출
    const getDeviceType = (userAgent: string): string => {
        if (userAgent.includes('Mobile')) return 'Mobile';
        if (userAgent.includes('Tablet')) return 'Tablet';
        return 'Desktop';
    };

    // 현재 디바이스 ID 가져오기
    const getCurrentDeviceId = (): string => {
        return getDeviceId() || '';
    };

    // 현재 디바이스인지 확인하는 함수
    const isCurrentDevice = (device: any): boolean => {
        return device.deviceId === getCurrentDeviceId();
    };

    // Fetch device list from API
    const fetchDeviceList = async () => {
        try {
            setLoading(true);
            setError(null);

            const accessToken = localStorage.getItem('accessToken');
            const deviceId = getDeviceId();

            const response = await axios.post(
                `${GK_API_URL}/${API_VERSION}/manage/device/getDeviceList`,
                {}, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                    'x-device-id': deviceId
                }
            });
            console.log(response.data.devices);
            if (response.data && response.data.message === "success") {
                // API 응답에서 디바이스 배열 추출
                const deviceDataList = response.data.devices || [];
                console.log('deviceDataList', deviceDataList);

                // 현재 디바이스를 맨 위로 정렬
                const sortedDevices = deviceDataList.sort((a: any, b: any) => {
                    const aIsCurrent = isCurrentDevice(a);
                    const bIsCurrent = isCurrentDevice(b);

                    if (aIsCurrent && !bIsCurrent) return -1;
                    if (!aIsCurrent && bIsCurrent) return 1;
                    return 0;
                });

                setDevices([...sortedDevices]);
            } else {
                console.log(response);
                setError('Failed to fetch device list');
            }
        } catch (err) {
            console.error('Error fetching device list:', err);
            setError('Failed to load device list');
            if (err.response.status === 401) {
                console.log('401 error');
                localStorage.removeItem('accessToken');
                localStorage.removeItem('userToken');
                window.location.reload();
            }
        } finally {
            setLoading(false);
        }
    };

    // Load device list on component mount
    useEffect(() => {
        fetchDeviceList();
    }, []);

    // Handle device removal
    const handleRemoveDevice = async (deviceId: string) => {
        try {
            // Set removing state and clear any previous errors
            setRemovingDevices(prev => new Set(prev).add(deviceId));
            setDeviceErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[deviceId];
                return newErrors;
            });

            const accessToken = localStorage.getItem('accessToken');
            const currentDeviceId = getDeviceId();

            const response = await axios.post(
                `${GK_API_URL}/${API_VERSION}/manage/device/deleteDevice`,
                { deviceId }, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                    'x-device-id': currentDeviceId
                }
            });
            console.log(response.data);
            if (response.data && response.data.message === "success") {
                setDevices(prevDevices => prevDevices.filter(device => device.deviceId !== deviceId));
            } else {
                console.error('Failed to remove device:', response.data);
                setDeviceErrors(prev => ({
                    ...prev,
                    [deviceId]: response.data?.message || '삭제에 실패했습니다.'
                }));
            }
        } catch (err) {
            console.error('Error removing device:', err);
            setDeviceErrors(prev => ({
                ...prev,
                [deviceId]: '네트워크 오류로 삭제에 실패했습니다.'
            }));
        } finally {
            setRemovingDevices(prev => {
                const newSet = new Set(prev);
                newSet.delete(deviceId);
                return newSet;
            });
        }
    };


    return (
        <Box className="box-settings security-container">
            {/* <Typography variant="h6" style={{ marginBottom: 10 }}>
                {t('settings.security.mfa')}
            </Typography>
            <Box className="container-security-mfa">

            </Box> */}

            <Typography variant="h6" className="security-device-title">
                {t('settings.security.login_device')}
            </Typography>

            {/* 디바이스 개수 표시 */}
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="textSecondary">
                    {t('settings.security.total_devices_logged_in', { count: devices.length })}
                </Typography>
                <Button
                    size="small"
                    startIcon={<Refresh />}
                    onClick={fetchDeviceList}
                    disabled={loading}
                    sx={{ ml: 'auto' }}
                >
                    {t('settings.security.refresh')}
                </Button>
            </Box>

            {devices.length === 0 ? (
                <LoadingComponent />
            ) : (
                <List>
                    {devices.map((device, index) => {
                        const userAgent = device.data?.extraData?.userAgent || '';
                        const os = getOSFromUserAgent(userAgent);
                        const deviceType = getDeviceType(userAgent);

                        return (
                            <React.Fragment key={device.deviceId || index}>
                                <div className="device-card">
                                    <div className="device-os-column">
                                        <Box className="device-os-content">
                                            {getOSIcon(os)}
                                            <Box className="device-os-text">
                                                <Typography variant="body2" fontWeight={600}>
                                                    {os}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {deviceType}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </div>

                                    <div className="device-info-column">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <Typography variant="body2" fontWeight={500} className="device-id">
                                                Device ID: {device.deviceId}
                                            </Typography>
                                            {isCurrentDevice(device) && (
                                                <Chip
                                                    label="현재 장비"
                                                    size="small"
                                                    color="primary"
                                                    variant="filled"
                                                    sx={{
                                                        fontSize: '0.7rem',
                                                        height: '20px'
                                                    }}
                                                />
                                            )}
                                        </Box>
                                        <Typography variant="body2" color="textSecondary" className="device-user-agent">
                                            {userAgent}
                                        </Typography>
                                    </div>

                                    <div className="device-action-column">
                                        {deviceErrors[device.deviceId] && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                                                <Typography
                                                    variant="caption"
                                                    color="error"
                                                    sx={{
                                                        fontSize: '0.7rem',
                                                        maxWidth: '100px',
                                                        textAlign: 'center'
                                                    }}
                                                >
                                                    {deviceErrors[device.deviceId]}
                                                </Typography>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setDeviceErrors(prev => {
                                                        const newErrors = { ...prev };
                                                        delete newErrors[device.deviceId];
                                                        return newErrors;
                                                    })}
                                                    sx={{ padding: 0, minWidth: 'auto' }}
                                                >
                                                    <Close fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        )}
                                        {!isCurrentDevice(device) && (
                                            <Tooltip title="Remove device">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleRemoveDevice(device.deviceId)}
                                                    className="device-remove-button"
                                                    disabled={removingDevices.has(device.deviceId)}
                                                >
                                                    {removingDevices.has(device.deviceId) ? (
                                                        <CircularProgress size={16} />
                                                    ) : (
                                                        <Delete fontSize="small" />
                                                    )}
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </div>
                                </div>
                                {index < devices.length - 1 && <Divider className="device-divider" />}
                            </React.Fragment>
                        );
                    })}
                </List>
            )}
        </Box>
    );
}

export default Security;